import {Component, OnInit} from '@angular/core';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {ActivatedRoute} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {Title} from '@angular/platform-browser';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {AppConfigService} from '../../../Services/app-config.service';

@Component({
  selector: 'app-provider-config',
  templateUrl: './provider-config.component.html',
  styleUrls: ['./provider-config.component.css'],
  standalone: false
})
export class ProviderConfigComponent implements OnInit {
  vm: any = {
    title: 'Payroll Provider Settings',
    config: null,
    isLoaded: false,
    buttonsDisabled: false,
    account: [],
    accountFilter: {search_string: '', type_name: ''},
    startAccount: 0,
    report: [],
  };

  glMappings: any[] = [];
  accounts: any[] = [];
  companies: any[] = [];
  newRow: any = {gusto_account_description: '', local_account_id: null, debit_credit: ''};
  showAddRow = false;
  activeGlRow: any = null;

  getRowType(row: any): 'Debit' | 'Credit' | null {
    if (!row.debit_credit) {
      return null;
    }
    return row.debit_credit === 'D' ? 'Debit' : 'Credit';
  }

  getAcctType(acct: any): 'Debit' | 'Credit' {
    return acct?.debit === true ? 'Debit' : 'Credit';
  }

  isMismatch(row: any): boolean {
    if (!row.local_account_id) {
      return false;
    }
    const gustoType = this.getRowType(row);
    if (!gustoType) {
      return false;
    }
    const acct = this.accounts.find((a: any) => a.id === row.local_account_id);
    if (acct == null) {
      return false;
    }
    return this.getAcctType(acct) !== gustoType;
  }

  onAccountChange(row: any) {
    if (this.isMismatch(row)) {
      const gustoType = this.getRowType(row);
      const acct = this.accounts.find((a: any) => a.id === row.local_account_id);
      const acctType = this.getAcctType(acct);
      this.notification.showWarningToast(
        $localize`:GLMappingMismatch@@glMappingMismatch:Type mismatch: Gusto "${row.gusto_account_description}" is ${gustoType} but selected account is ${acctType}.`
      );
    }
  }

  openAccountModal(row: any) {
    this.activeGlRow = row;
    this.vm.accountFilter = {search_string: '', type_name: ''};
    this.vm.startAccount = 0;
  }

  onGlAccountSelected(accountId: string) {
    if (!this.activeGlRow) {
      return;
    }
    this.activeGlRow.local_account_id = accountId;
    this.onAccountChange(this.activeGlRow);
  }

  getAccountLabel(row: any): string {
    if (!row.local_account_id) {
      return '— None —';
    }
    const acct = this.accounts.find((a: any) => a.id === row.local_account_id);
    return acct ? `${acct.number} - ${acct.name}` : '— None —';
  }

  providerOptions = [
    {value: 'gusto', label: 'Gusto'},
    {value: 'adp', label: 'ADP (Coming Soon)'},
    {value: 'paychex', label: 'Paychex (Coming Soon)'},
  ];

  apiBaseUrl: string;
  showSecret = false;

  constructor(
    private titleService: Title,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private appConfig: AppConfigService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private confirm: ConfirmDialogService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.vm.title);
    this.apiBaseUrl = this.appConfig.apiBaseUrl;
    this.loadConfig();
    this.loadAccounts();
    this.loadMappings();
    this.loadCompanies();
    if (this.route.snapshot.queryParamMap.get('gusto') === 'connected') {
      this.notification.showSuccessToast($localize`:GustoConnected@@gustoConnected:Gusto connected successfully.`);
    }
  }

  loadConfig() {
    this.tableDataService.getData('pr_provider_config').subscribe({
      next: (result: any) => {
        this.vm.config = result;
        // Map is_sandbox from API to gusto_environment for the dropdown
        this.vm.config.gusto_environment = result.is_sandbox === false ? 'production' : 'demo';
        this.vm.isLoaded = true;
      },
      error: (err) => {
        console.error(err);
        // No config yet — show create form
        this.vm.config = {
          provider: 'gusto',
          active: false,
          client_id: null,
          client_secret: null,
          redirect_uri: null,
          gusto_company_uuid: null,
          gusto_environment: 'demo'
        };
        this.vm.isLoaded = true;
      },
    });
  }

  get isConnected(): boolean {
    return !!(this.vm.config?.active && this.vm.config?.gusto_company_uuid);
  }

  get tokenExpiry(): string | null {
    return this.vm.config?.token_expiry ?? null;
  }

  saveConfig() {
    if (!this.vm.config || this.vm.buttonsDisabled) {
      return;
    }
    this.vm.buttonsDisabled = true;

    const payload = {...this.vm.config, is_sandbox: this.vm.config.gusto_environment !== 'production'};
    if (!this.vm.config.id) {
      this.tableDataService.post('pr_provider_config', payload).subscribe({
        next: () => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          this.vm.buttonsDisabled = false;
        },
        error: (err) => {
          console.error(err);
          this.vm.buttonsDisabled = false;
        },
      });
    } else {
      this.tableDataService.put('pr_provider_config', this.vm.config.id, payload).subscribe({
        next: () => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          this.vm.buttonsDisabled = false;
        },
        error: (err) => {
          console.error(err);
          this.vm.buttonsDisabled = false;
        },
      });
    }
  }

  connectGusto() {
    if (!this.vm.config?.client_id || !this.vm.config?.redirect_uri) {
      this.notification.showErrorToast($localize`:MissingGustoCredentials@@missingGustoCredentials:Please save Client ID and Redirect URI before connecting.`);
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmConnectGusto@@confirmConnectGusto:Connect to Gusto? You will be redirected to Gusto's authorization page.`],
      () => {
    // Call connect endpoint with Bearer token (registers state on backend), then redirect directly to Gusto OAuth.
    const env = this.vm.config?.gusto_environment ?? 'demo';
    this.http.get<any>(this.apiBaseUrl + 'Gusto/connect?environment=' + env).subscribe({
      next: () => {
        const isDemo = this.vm.config?.gusto_environment !== 'production';
        const baseUrl = isDemo ? 'https://api.gusto-demo.com' : 'https://api.gusto.com';
        const params = new URLSearchParams({
          client_id: this.vm.config.client_id,
          redirect_uri: this.vm.config.redirect_uri,
          response_type: 'code',
        });
        window.location.href = `${baseUrl}/oauth/authorize?${params.toString()}`;
      },
      error: (err) => {
        console.error('Failed to initiate Gusto connection', err);
        this.notification.showErrorToast($localize`:GustoConnectError@@gustoConnectError:Failed to initiate Gusto connection.`);
      },
    });
      },
      () => {
      }
    );
  }

  syncPayroll() {
    if (this.vm.buttonsDisabled) {
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmSyncPayroll@@confirmSyncPayroll:Sync payroll data from Gusto? This may take a moment.`],
      () => {
    this.vm.buttonsDisabled = true;
        this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/sync-payroll', {}).subscribe({
      next: (res) => {
        this.notification.showSuccessToast(res?.message ?? $localize`:PayrollSynced@@payrollSynced:Payroll synced from Gusto.`);
        this.vm.buttonsDisabled = false;
        this.loadConfig();
      },
      error: (err) => {
        console.error(err);
        const msg = err?.error?.message ?? err?.message ?? $localize`:ErrorSyncingPayroll@@errorSyncingPayroll:Error syncing payroll from Gusto.`;
        this.notification.showErrorToast(msg);
        this.vm.buttonsDisabled = false;
      },
        });
      },
      () => {
      }
    );
  }

  isPostingGl = false;

  postGl() {
    if (this.vm.buttonsDisabled) {
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmPostGL@@confirmPostGL:Post GL entries to AcctX from Gusto payroll data?`],
      () => {
        this.vm.buttonsDisabled = true;
        this.isPostingGl = true;
        this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/sync-gl', {}).subscribe({
          next: (res) => {
            this.notification.showSuccessToast(res?.message ?? $localize`:GLPosted@@glPosted:GL posted to AcctX.`);
        this.vm.buttonsDisabled = false;
            this.isPostingGl = false;
            this.loadMappings();
          },
          error: (err) => {
            const msg = err?.error?.message ?? err?.message ?? $localize`:GLPostError@@glPostError:GL sync failed.`;
            this.notification.showErrorToast(msg);
            this.vm.buttonsDisabled = false;
            this.isPostingGl = false;
          },
        });
      },
      () => {
      }
    );
  }

  loadCompanies() {
    this.tableDataService.getApiListData('company', '?pageNumber=0&pageSize=999999').subscribe((result: any) => {
      this.companies = result?.Data ?? [];
    });
  }

  loadAccounts() {
    this.tableDataService.getApiListData('account', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (res: any) => {
        this.accounts = res?.Data ?? res ?? [];
        this.vm.account = this.accounts;
        this.vm.startAccount = 0;
        this.vm.accountFilter = {search_string: '', type_name: ''};
      },
      error: (err) => console.error(err),
    });
  }

  loadMappings() {
    this.http.get<any[]>(this.apiBaseUrl + 'Gusto/gl-account-mappings').subscribe({
      next: (rows) => {
        this.glMappings = (rows || []).map((r: any) => ({...r}));
      },
      error: (err) => console.error(err),
    });
  }

  saveMapping(row: any) {
    const dto: any = {gusto_account_description: row.gusto_account_description, local_account_id: row.local_account_id};
    if (row.debit_credit) {
      dto.debit_credit = row.debit_credit;
    }
    this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/gl-account-mappings', dto).subscribe({
      next: () => this.loadMappings(),
      error: (err) => {
        const msg = err?.error?.message ?? err?.message ?? $localize`:SaveMappingError@@saveMappingError:Error saving mapping.`;
        this.notification.showErrorToast(msg);
      },
    });
  }

  deleteMapping(row: any) {
    if (!row.id) {
      row.local_account_id = null;
      return;
    }
    this.http.delete(this.apiBaseUrl + 'Gusto/gl-account-mappings/' + row.id).subscribe({
      next: () => this.loadMappings(),
      error: (err) => {
        const msg = err?.error?.message ?? err?.message ?? $localize`:DeleteMappingError@@deleteMappingError:Error deleting mapping.`;
        this.notification.showErrorToast(msg);
      },
    });
  }

  confirmRemoveRow(row: any) {
    this.confirm.confirmThis(
      [$localize`:ConfirmRemoveRow@@confirmRemoveRow:Remove this GL mapping row?`],
      () => this.removeUserRow(row),
      () => {
      }
    );
  }

  removeUserRow(row: any) {
    if (row.id) {
      this.http.delete(this.apiBaseUrl + 'Gusto/gl-account-mappings/' + row.id).subscribe({
        next: () => this.glMappings = this.glMappings.filter(r => r !== row),
        error: (err) => {
          const msg = err?.error?.message ?? err?.message ?? $localize`:DeleteMappingError@@deleteMappingError:Error deleting mapping.`;
          this.notification.showErrorToast(msg);
        },
      });
    } else {
      this.glMappings = this.glMappings.filter(r => r !== row);
    }
  }

  saveAllMappings() {
    const rows = this.glMappings.filter(r => r.local_account_id);
    if (!rows.length) {
      this.notification.showWarningToast($localize`:NoMappingsToSave@@noMappingsToSave:No mapped accounts to save.`);
      return;
    }
    let completed = 0;
    rows.forEach(row => {
      const dto = {gusto_account_description: row.gusto_account_description, local_account_id: row.local_account_id};
      this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/gl-account-mappings', dto).subscribe({
        next: () => {
          completed++;
          if (completed === rows.length) {
            this.notification.showSuccessToast($localize`:AllMappingsSaved@@allMappingsSaved:All mappings saved.`);
            this.loadMappings();
          }
        },
        error: (err) => {
          const msg = err?.error?.message ?? err?.message ?? $localize`:SaveMappingError@@saveMappingError:Error saving mapping.`;
          this.notification.showErrorToast(msg);
        },
      });
    });
  }

  addNewRow() {
    if (!this.newRow.gusto_account_description.trim()) {
      return;
    }
    this.saveMapping(this.newRow);
    this.newRow = {gusto_account_description: '', local_account_id: null, debit_credit: ''};
    this.showAddRow = false;
  }

  syncLocations() {
    if (this.vm.buttonsDisabled) {
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmSyncLocations@@confirmSyncLocations:Sync locations from Gusto?`],
      () => {
    this.vm.buttonsDisabled = true;
    this.tableDataService.post('Gusto/sync-locations', {}).subscribe({
      next: () => {
        this.notification.showSuccessToast($localize`:Locations synced@@locationsSynced:Locations synced`);
        this.vm.buttonsDisabled = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast($localize`:Error syncing locations@@errorSyncingLocations:Error syncing locations`);
        this.vm.buttonsDisabled = false;
      },
    });
      },
      () => {
      }
    );
  }
}
